import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  buyBed,
  buyHospitalBay,
  finishHospitalBayNow,
  buyHospitalSpecialty,
  buyHospitalApparatus,
  sellHospitalApparatus,
  buyHospitalLandingPad,
  patchHospital,
  sellHospital,
  dischargePatient,
  renameAllianceHospital,
  getState,
} from "@/lib/api";
import {
  HOSPITAL_MAX_BAYS,
  HOSPITAL_SPECIALTIES,
  HOSPITAL_SPECIALTY_COST,
  HOSPITAL_BAY_COST,
  APPARATUS_COST,
  bedCost,
  gemCost,
  hospitalFreeBeds,
} from "@/lib/gameConfig";
import { toast } from "sonner";
import { Plus, Trash, CurrencyDollar, Diamond, Heartbeat, Bed, Star, Buildings, PencilSimple, Check, X } from "@phosphor-icons/react";

const fmtETA = (iso) => {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "done";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

export default function HospitalDialog({ open, onOpenChange, hospital, player, onPlayerUpdate, readOnly = false, ownerName = null, allianceId = null, canEditAllianceHospital = false, onAllianceHospitalChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmSellId, setConfirmSellId] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");

  // 1s tick so pending-bay countdown updates live.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  // Auto-promote pending bays once their timer elapses by polling state.
  useEffect(() => {
    if (!open || !hospital) return;
    const id = setInterval(async () => {
      const pending = hospital.bays_pending || [];
      if (pending.some((p) => new Date(p.completes_at) <= new Date())) {
        try {
          const r = await getState();
          if (r.state) onPlayerUpdate({
            ...player,
            hospitals: r.state.hospitals,
            stations: r.state.stations,
            money: r.state.money,
            gems: r.state.gems,
          });
        } catch (_e) { /* ignore */ }
      }
    }, 5000);
    return () => clearInterval(id);
  }, [open, hospital, player, onPlayerUpdate]);

  useEffect(() => {
    if (!confirmSellId) return;
    const id = setTimeout(() => setConfirmSellId(null), 4000);
    return () => clearTimeout(id);
  }, [confirmSellId]);

  if (!hospital) return null;
  const trucks = hospital.trucks || [];
  const bays = hospital.bays || 0;
  const bays_pending = hospital.bays_pending || [];
  const totalBays = bays + bays_pending.length;
  const beds = hospital.beds || 0;
  const free = hospitalFreeBeds(hospital);
  const specs = hospital.specialties || [];
  const remainingSpecs = HOSPITAL_SPECIALTIES.filter((s) => !specs.includes(s));
  const nextBed = bedCost(beds);

  // Landing Pad state (capped at 2). HEMS units stored on pads do NOT count
  // against `bays`/`trucks` capacity for ambulances.
  const HOSPITAL_LANDING_PAD_COST = 300000;
  const HOSPITAL_MAX_LANDING_PADS = 2;
  const landingPads = Number(hospital.landing_pads || 0);
  const hemsTrucks = trucks.filter((t) => t.type === "HEMS").length;
  const emptyLandingPads = Math.max(0, landingPads - hemsTrucks);
  const canBuyLandingPad = landingPads < HOSPITAL_MAX_LANDING_PADS;

  const handleBuyBed = async (payWith = "money") => {
    setBusy(true);
    try {
      const updated = await buyBed(hospital.id, payWith);
      onPlayerUpdate(updated);
      toast.success(`Added 1 bed (now ${updated.hospitals.find((h) => h.id === hospital.id)?.beds})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bed purchase failed");
    } finally {
      setBusy(false);
    }
  };
  const handleBuyBay = async (payWith = "money") => {
    setBusy(true);
    try {
      const updated = await buyHospitalBay(hospital.id, payWith);
      onPlayerUpdate(updated);
      const next = updated.hospitals.find((h) => h.id === hospital.id);
      const isPending = (next?.bays_pending || []).length > (bays_pending.length);
      toast.success(isPending ? "Bay queued (24h build)" : "Bay added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bay purchase failed");
    } finally {
      setBusy(false);
    }
  };
  const handleFinishBay = async (pid) => {
    setBusy(true);
    try {
      const updated = await finishHospitalBayNow(hospital.id, pid);
      onPlayerUpdate(updated);
      toast.success("Bay completed (-50 gems)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Finish failed");
    } finally {
      setBusy(false);
    }
  };
  const handleBuyLandingPad = async (payWith = "money") => {
    setBusy(true);
    try {
      const updated = await buyHospitalLandingPad(hospital.id, payWith);
      onPlayerUpdate(updated);
      const next = updated.hospitals.find((h) => h.id === hospital.id);
      toast.success(`Landing Pad built (${next?.landing_pads}/${HOSPITAL_MAX_LANDING_PADS})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Landing Pad purchase failed");
    } finally {
      setBusy(false);
    }
  };
  const handleBuySpecialty = async (sp, payWith = "money") => {
    setBusy(true);
    try {
      const updated = await buyHospitalSpecialty(hospital.id, sp, payWith);
      onPlayerUpdate(updated);
      toast.success(`${sp} unlocked`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Specialty purchase failed");
    } finally {
      setBusy(false);
    }
  };
  const handleDischarge = async (bed) => {
    setBusy(true);
    try {
      const res = await dischargePatient(hospital.id, bed.patient_id);
      // Server returns gem_cost only; we need to refresh state via getState
      // so player gems + hospitals are both updated atomically.
      const fresh = await getState();
      // fresh.state contains money/gems/hospitals — we want to feed it back
      // through onPlayerUpdate just like other handlers.
      onPlayerUpdate({
        ...fresh.state,
        // backend returns {state}, but onPlayerUpdate expects the full
        // game-state object (money/gems/stations/hospitals/etc).
      });
      toast.success(`Patient discharged (-${res.gem_cost} ◆)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discharge failed");
    } finally {
      setBusy(false);
    }
  };
  const handleBuyMedic = async () => {
    setBusy(true);
    try {
      const updated = await buyHospitalApparatus(hospital.id, { type: "Medic", pay_with: "money" });
      onPlayerUpdate(updated);
      toast.success("Medic purchased");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not buy Medic");
    } finally {
      setBusy(false);
    }
  };
  const handleBuyApparatusGeneric = async (type, payWith = "money") => {
    setBusy(true);
    try {
      const updated = await buyHospitalApparatus(hospital.id, { type, pay_with: payWith });
      onPlayerUpdate(updated);
      toast.success(`${type} purchased`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not buy ${type}`);
    } finally {
      setBusy(false);
    }
  };
  const handleSellMedic = async (truck) => {
    setBusy(true);
    setConfirmSellId(null);
    try {
      const updated = await sellHospitalApparatus(hospital.id, truck.id);
      onPlayerUpdate(updated);
      toast.success(`Sold ${truck.name} — refunded $${(APPARATUS_COST.Medic / 2).toLocaleString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sell failed");
    } finally {
      setBusy(false);
    }
  };
  const handleToggleShare = async () => {
    try {
      const updated = await patchHospital(hospital.id, { shared_with_alliance: !hospital.shared_with_alliance });
      onPlayerUpdate(updated);
      toast.success(hospital.shared_with_alliance ? "Alliance sharing OFF" : "Alliance sharing ON");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  // Allow inline rename for player hospitals always, and for alliance
  // hospitals only when the viewer is Chief / Deputy Chief.
  const canRename = !readOnly || canEditAllianceHospital;
  const isAllianceOwned = !!allianceId;
  const handleStartRename = () => {
    setDraftName(hospital.name || "");
    setEditingName(true);
  };
  const handleSaveRename = async () => {
    const trimmed = (draftName || "").trim().slice(0, 60);
    if (!trimmed || trimmed === hospital.name) {
      setEditingName(false);
      return;
    }
    setBusy(true);
    try {
      if (isAllianceOwned) {
        await renameAllianceHospital(allianceId, hospital.id, trimmed);
        onAllianceHospitalChanged && onAllianceHospitalChanged({ id: hospital.id, name: trimmed });
      } else {
        const updated = await patchHospital(hospital.id, { name: trimmed });
        onPlayerUpdate && onPlayerUpdate(updated);
      }
      toast.success(`Renamed to "${trimmed}"`);
      setEditingName(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };
  const handleCancelRename = () => { setEditingName(false); setDraftName(""); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="hospital-dialog"
        className="rounded-none border border-white/10 bg-[#0F1115] text-white max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="mono text-[12px] uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2">
            <Buildings size={16} className={readOnly && !canEditAllianceHospital ? "text-cyan-400" : "text-pink-400"} />
            {editingName ? (
              <span className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  data-testid="hospital-name-input"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSaveRename(); if (e.key === "Escape") handleCancelRename(); }}
                  autoFocus
                  maxLength={60}
                  className="bg-zinc-900 border border-pink-500/40 text-zinc-200 mono text-[11px] tracking-wider px-2 py-0.5 flex-1 min-w-0 focus:outline-none focus:border-pink-400"
                />
                <button type="button" data-testid="hospital-name-save" onClick={handleSaveRename} disabled={busy} className="text-emerald-400 hover:text-emerald-300 p-1" title="Save"><Check size={13} weight="bold" /></button>
                <button type="button" onClick={handleCancelRename} className="text-zinc-500 hover:text-zinc-300 p-1" title="Cancel"><X size={13} weight="bold" /></button>
              </span>
            ) : (
              <>
                <span className="truncate flex-1">{hospital.name}</span>
                {canRename && (
                  <button type="button" data-testid="hospital-name-edit" onClick={handleStartRename} className="text-zinc-500 hover:text-emerald-400 p-1" title="Rename hospital">
                    <PencilSimple size={12} />
                  </button>
                )}
              </>
            )}
            {readOnly && !editingName && (
              <span className="ml-auto mono text-[9px] uppercase tracking-widest text-cyan-300 truncate">
                Group{canEditAllianceHospital ? " · Staff" : " · View Only"}{ownerName ? ` · ${ownerName}` : ""}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-3">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-2">
            <Stat label="Beds" value={`${free}/${beds}`} />
            <Stat label="Bays" value={`${bays}${bays_pending.length ? ` (+${bays_pending.length})` : ""} / ${HOSPITAL_MAX_BAYS}`} />
            <Stat label="Medics" value={trucks.length} />
            <Stat label="Specs" value={specs.length} />
          </div>

          {/* Alliance share toggle */}
          {!readOnly && (
            <button
              type="button"
              data-testid="hospital-share-toggle"
              onClick={handleToggleShare}
              className={`w-full border px-3 py-2 mono text-[10px] uppercase tracking-widest ${
                hospital.shared_with_alliance
                  ? "border-emerald-400 text-emerald-300 bg-emerald-400/10"
                  : "border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              {hospital.shared_with_alliance ? "✓ Shared with Mutual Aid Group" : "Share with Mutual Aid Group"}
            </button>
          )}

          {/* Beds */}
          <Section icon={<Bed size={12} />} title={`Beds (${beds})`}>
            <div className={`grid grid-cols-2 gap-1.5 ${readOnly ? "hidden" : ""}`}>
              <Button
                data-testid="hospital-buy-bed"
                onClick={() => handleBuyBed("money")}
                disabled={busy || (player?.money ?? 0) < nextBed}
                className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40"
              >
                <Plus size={12} className="mr-1" /> ${nextBed.toLocaleString()}
              </Button>
              <Button
                data-testid="hospital-buy-bed-gems"
                onClick={() => handleBuyBed("gems")}
                disabled={busy || (player?.gems ?? 0) < gemCost(nextBed)}
                className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-cyan-500 hover:bg-cyan-400 text-black disabled:opacity-40"
              >
                <Diamond size={11} weight="fill" className="mr-1" /> {gemCost(nextBed).toLocaleString()}
              </Button>
            </div>
            {/* Occupied beds — show ETA + instant-discharge gem button */}
            {(hospital.occupied_beds || []).length > 0 && (
              <div className="space-y-1 mt-1.5 pt-1.5 border-t border-white/5">
                <div className="mono text-[8px] uppercase tracking-widest text-zinc-500">
                  Occupied ({(hospital.occupied_beds || []).length})
                </div>
                {(hospital.occupied_beds || []).map((b) => {
                  const isSpec = !!b.specialty;
                  const gemCostDischarge = isSpec ? 2 : 1;
                  return (
                    <div
                      key={b.patient_id}
                      className="flex items-center gap-1.5 mono text-[9px] tracking-wider border border-white/5 px-1.5 py-1"
                      data-testid={`occupied-bed-${b.patient_id}`}
                    >
                      <Bed size={10} className="text-pink-400 shrink-0" />
                      <span className="text-zinc-400 truncate flex-1">
                        {isSpec ? (
                          <span className="text-amber-300 inline-flex items-center gap-0.5">
                            <Star size={8} weight="fill" /> {b.specialty}
                          </span>
                        ) : (
                          <span className="text-zinc-500">Std</span>
                        )}
                        {" · frees "}{fmtETA(b.freed_at)}
                      </span>
                      <button
                        type="button"
                        data-testid={`discharge-${b.patient_id}`}
                        onClick={() => handleDischarge(b)}
                        disabled={readOnly || busy || (player?.gems ?? 0) < gemCostDischarge}
                        title={`Instant discharge (-${gemCostDischarge} gem${gemCostDischarge > 1 ? "s" : ""})`}
                        className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-40 inline-flex items-center gap-0.5 ${readOnly ? "hidden" : ""}`}
                      >
                        <Diamond size={8} weight="fill" /> {gemCostDischarge}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Specialties */}
          <Section icon={<Star size={12} />} title="Specialties">
            <div className="space-y-1">
              {specs.map((s) => (
                <div key={s} className="border border-emerald-400/30 px-2 py-1 mono text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-2">
                  <Star size={10} weight="fill" /> {s}
                </div>
              ))}
              {remainingSpecs.length === 0 && specs.length > 0 && (
                <div className="mono text-[9px] uppercase text-zinc-600 text-center">All unlocked</div>
              )}
              {!readOnly && remainingSpecs.map((s) => (
                <div key={s} className="grid grid-cols-[1fr_auto_auto] gap-1.5 items-stretch">
                  <div className="mono text-[10px] uppercase tracking-widest text-amber-300 flex items-center px-2 border border-amber-400/40">
                    + {s}
                  </div>
                  <Button
                    data-testid={`hospital-buy-specialty-${s.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => handleBuySpecialty(s, "money")}
                    disabled={busy || (player?.money ?? 0) < HOSPITAL_SPECIALTY_COST}
                    className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-transparent border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 disabled:opacity-40 px-2"
                  >
                    ${HOSPITAL_SPECIALTY_COST.toLocaleString()}
                  </Button>
                  <Button
                    data-testid={`hospital-buy-specialty-gems-${s.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => handleBuySpecialty(s, "gems")}
                    disabled={busy || (player?.gems ?? 0) < gemCost(HOSPITAL_SPECIALTY_COST)}
                    className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-transparent border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-40 px-2 inline-flex items-center gap-0.5"
                  >
                    <Diamond size={10} weight="fill" /> {gemCost(HOSPITAL_SPECIALTY_COST).toLocaleString()}
                  </Button>
                </div>
              ))}
            </div>
          </Section>

          {/* Bays + Medics */}
          <Section icon={<Heartbeat size={12} />} title={`Medic Bays (${trucks.length}/${bays})`}>
            {trucks.map((t) => {
              const armed = confirmSellId === t.id;
              const refund = Math.floor((APPARATUS_COST.Medic || 0) / 2);
              return (
                <div key={t.id} className="flex items-center gap-2 mono text-[11px] tracking-wider border border-white/5 px-2 py-1.5">
                  <span className="text-pink-400">●</span>
                  <span className="flex-1 truncate">{t.name}</span>
                  {!readOnly && armed ? (
                    <>
                      <button
                        type="button"
                        data-testid={`hospital-sell-confirm-${t.id}`}
                        onClick={() => handleSellMedic(t)}
                        className="mono text-[9px] uppercase tracking-widest px-1.5 py-1 border border-red-500/60 text-red-300 bg-red-500/10 hover:bg-red-500/20"
                      >
                        Sell ${refund.toLocaleString()}?
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmSellId(null)}
                        className="border border-white/10 text-zinc-400 hover:text-white p-1"
                      >
                        <Trash size={10} />
                      </button>
                    </>
                  ) : !readOnly ? (
                    <button
                      type="button"
                      data-testid={`hospital-sell-${t.id}`}
                      onClick={() => setConfirmSellId(t.id)}
                      title={`Sell for $${refund.toLocaleString()}`}
                      className="text-zinc-500 hover:text-red-400 p-1"
                    >
                      <CurrencyDollar size={12} />
                    </button>
                  ) : null}
                </div>
              );
            })}
            {!readOnly && trucks.length < bays && (
              <Button
                data-testid="hospital-buy-medic"
                onClick={handleBuyMedic}
                disabled={busy}
                className="rounded-none w-full h-8 mono uppercase text-[10px] tracking-widest bg-pink-500 hover:bg-pink-400 text-black"
              >
                <Plus size={12} className="mr-1" /> Buy Medic — ${APPARATUS_COST.Medic.toLocaleString()}
              </Button>
            )}
            {bays_pending.map((p) => (
              <div key={p.id} className="border border-amber-500/30 px-2 py-1 mono text-[10px] uppercase tracking-widest text-amber-300 flex items-center gap-2">
                <span className="flex-1">Bay building… {fmtETA(p.completes_at)}</span>
                {!readOnly && (
                  <button
                    type="button"
                    data-testid={`hospital-finish-bay-${p.id}`}
                    onClick={() => handleFinishBay(p.id)}
                    className="border border-cyan-400/60 text-cyan-300 px-2 py-0.5 hover:bg-cyan-400/10"
                    title="Finish now (50 gems)"
                  >
                    ◆50
                  </button>
                )}
              </div>
            ))}
            {!readOnly && totalBays < HOSPITAL_MAX_BAYS && (
              <div className="grid grid-cols-2 gap-1.5">
                <Button
                  data-testid="hospital-buy-bay"
                  onClick={() => handleBuyBay("money")}
                  disabled={busy || (player?.money ?? 0) < HOSPITAL_BAY_COST}
                  className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-transparent border border-white/20 text-zinc-300 hover:bg-white/5 disabled:opacity-40"
                >
                  <Plus size={12} className="mr-1" /> ${HOSPITAL_BAY_COST.toLocaleString()} {totalBays === 0 ? "(instant)" : "(24h)"}
                </Button>
                <Button
                  data-testid="hospital-buy-bay-gems"
                  onClick={() => handleBuyBay("gems")}
                  disabled={busy || (player?.gems ?? 0) < gemCost(HOSPITAL_BAY_COST)}
                  className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-transparent border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-40 inline-flex items-center gap-0.5"
                >
                  <Diamond size={11} weight="fill" /> {gemCost(HOSPITAL_BAY_COST).toLocaleString()} {totalBays === 0 ? "(instant)" : "(24h)"}
                </Button>
              </div>
            )}
          </Section>

          {/* Landing Pads — up to 2 per hospital; each unlocks 1 HEMS slot. */}
          <Section icon={<Heartbeat size={12} />} title={`Landing Pads (${landingPads}/${HOSPITAL_MAX_LANDING_PADS})`}>
            {trucks.filter((t) => t.type === "HEMS").map((t) => {
              const armed = confirmSellId === t.id;
              const refund = Math.floor((APPARATUS_COST.HEMS || 0) / 2);
              return (
                <div key={t.id} className="flex items-center gap-2 mono text-[11px] tracking-wider border border-white/5 px-2 py-1.5">
                  <span style={{ color: "#A6F11C" }}>●</span>
                  <span className="flex-1 truncate">{t.name} <span className="text-zinc-500">· HEMS</span></span>
                  {!readOnly && armed ? (
                    <>
                      <button
                        type="button"
                        data-testid={`hospital-hems-sell-confirm-${t.id}`}
                        onClick={() => handleSellMedic(t)}
                        className="mono text-[9px] uppercase tracking-widest px-1.5 py-1 border border-red-500/60 text-red-300 bg-red-500/10 hover:bg-red-500/20"
                      >
                        Sell ${refund.toLocaleString()}?
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmSellId(null)}
                        className="border border-white/10 text-zinc-400 hover:text-white p-1"
                      >
                        <Trash size={10} />
                      </button>
                    </>
                  ) : !readOnly ? (
                    <button
                      type="button"
                      data-testid={`hospital-hems-sell-${t.id}`}
                      onClick={() => setConfirmSellId(t.id)}
                      title={`Sell for $${refund.toLocaleString()}`}
                      className="text-zinc-500 hover:text-red-400 p-1"
                    >
                      <CurrencyDollar size={12} />
                    </button>
                  ) : null}
                </div>
              );
            })}
            {!readOnly && emptyLandingPads > 0 && (
              <Button
                data-testid="hospital-buy-hems"
                onClick={() => handleBuyApparatusGeneric("HEMS")}
                disabled={busy}
                className="rounded-none w-full h-8 mono uppercase text-[10px] tracking-widest bg-lime-400 hover:bg-lime-300 text-black"
              >
                <Plus size={12} className="mr-1" /> Buy HEMS — ${APPARATUS_COST.HEMS.toLocaleString()}
              </Button>
            )}
            {!readOnly && canBuyLandingPad && (
              <div className="grid grid-cols-2 gap-1.5">
                <Button
                  data-testid="hospital-buy-landing-pad"
                  onClick={() => handleBuyLandingPad("money")}
                  disabled={busy || (player?.money ?? 0) < HOSPITAL_LANDING_PAD_COST}
                  className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-transparent border border-lime-400/40 text-lime-300 hover:bg-lime-400/10 disabled:opacity-40"
                >
                  <Plus size={12} className="mr-1" /> Landing Pad ${HOSPITAL_LANDING_PAD_COST.toLocaleString()}
                </Button>
                <Button
                  data-testid="hospital-buy-landing-pad-gems"
                  onClick={() => handleBuyLandingPad("gems")}
                  disabled={busy || (player?.gems ?? 0) < gemCost(HOSPITAL_LANDING_PAD_COST)}
                  className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-transparent border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-40 inline-flex items-center gap-0.5"
                >
                  <Diamond size={11} weight="fill" /> {gemCost(HOSPITAL_LANDING_PAD_COST).toLocaleString()}
                </Button>
              </div>
            )}
            {landingPads === 0 && (
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 px-1">
                Each Landing Pad unlocks 1 HEMS helicopter housing slot.
              </div>
            )}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 px-2 py-1.5">
      <div className="mono text-[8px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-[12px] font-bold mono tracking-wider">{value}</div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="space-y-1.5">
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}
